export type MpReviewReason = 'sensitive' | 'manual' | null;

export function formatMpSensitiveWordsInline(words: string[] | undefined): string {
  const list = (words ?? []).map((w) => w.trim()).filter(Boolean);
  if (list.length === 0) return '';
  return list.join('、');
}

export function MicroPromotionReviewAlert({
  servingStatus,
  reviewReason,
  matchedWords,
}: {
  servingStatus: string;
  reviewReason?: MpReviewReason;
  matchedWords?: string[];
}) {
  if (servingStatus !== 'pending_review') return null;

  const words = (matchedWords ?? []).map((w) => w.trim()).filter(Boolean);
  const isSensitive = reviewReason === 'sensitive' || words.length > 0;

  return (
    <div
      className="rounded-xl border border-amber-200/90 bg-amber-50/95 px-4 py-3.5 shadow-sm"
      role="alert"
    >
      <div className="flex gap-3">
        <span
          className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-800"
          aria-hidden
        >
          <i className="fa-solid fa-triangle-exclamation text-sm" />
        </span>
        <div className="min-w-0 flex-1 space-y-2">
          <p className="text-sm font-semibold text-amber-950">
            {isSensitive ? '待审核：命中敏感词' : '待人工审核'}
          </p>
          <p className="text-sm leading-relaxed text-amber-900/95">
            {isSensitive
              ? '请修改正文后保存，或由管理员 / 站长审核通过后再进入 Android 推广队列与企微推送。'
              : '请核对内容后，由管理员或站长审核通过后再进入推广队列。'}
          </p>
          {words.length > 0 ? (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs font-medium text-amber-900/80">命中词：</span>
              {words.map((w) => (
                <span
                  key={w}
                  className="inline-flex max-w-full items-center rounded-lg border border-amber-300/80 bg-white px-2.5 py-1 font-mono text-xs font-medium text-amber-950 shadow-sm"
                >
                  {w}
                </span>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function mpServingStatusBadgeClass(status: string): string {
  if (status === 'active') return 'bg-emerald-50 text-emerald-900 ring-emerald-200/80';
  if (status === 'paused') return 'bg-amber-50 text-amber-900 ring-amber-200/80';
  if (status === 'pending_review') return 'bg-violet-50 text-violet-900 ring-violet-200/80';
  if (status === 'review_rejected') return 'bg-rose-50 text-rose-900 ring-rose-200/80';
  return 'bg-slate-100 text-slate-600 ring-slate-200/80';
}

export function mpServingStatusLabel(status: string, fallback?: string): string {
  if (fallback) return fallback;
  const map: Record<string, string> = {
    active: '推广中',
    paused: '已暂停',
    completed: '已完成',
    pending_review: '待审核',
    review_rejected: '审核已拒绝',
  };
  return map[status] ?? status;
}
